import React, { useContext } from "react";
import { styled, useTheme } from "@mui/material/styles";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../features/authSlice";
import { Tooltip } from "@mui/material";
import { ColorModeContext } from "../context/ColorModeContext";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open, drawerwidth }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerwidth,
    width: `calc(100% - ${drawerwidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

export default function Header({ open, handleDrawerOpen, drawerWidth }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);

  const [anchorEl, setAnchorEl] = React.useState(null);
  const openMenu = Boolean(anchorEl);

  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleCloseMenu();
    dispatch(logoutUser());
  };

  const getInitials = (name = "") => {
    return name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0])
      .join(" ")
      .toUpperCase();
  };

  return (
    <AppBar
      position="fixed"
      open={open}
      drawerwidth={drawerWidth}
      sx={{
        transition: "background-color 0.3s ease",
      }}
    >
      <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
        {/* Left side: Menu + Logo */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {!open && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{ marginRight: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" noWrap component="div">
            لوحة التحكم - سند
          </Typography>
        </Box>

        {/* Right side: Dark Mode Toggle + Username + Avatar */}
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {/* Dark / Light mode toggle */}
          <Tooltip
            title={
              theme.palette.mode === "dark" ? "وضع النهار" : "الوضع الداكن"
            }
          >
            <IconButton color="inherit" onClick={colorMode.toggleColorMode}>
              {theme.palette.mode === "dark" ? (
                <LightModeIcon />
              ) : (
                <DarkModeIcon />
              )}
            </IconButton>
          </Tooltip>

          {/* User Info */}
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            onClick={handleMenuClick}
            sx={{ cursor: "pointer" }}
          >
            <Typography variant="body1" color="inherit">
              {user.name}
            </Typography>
            <Avatar sx={{ width: 56, height: 56, mx: "auto", mb: 1 }}>
              {getInitials(user.name)}
            </Avatar>
          </Stack>

          {/* Dropdown Menu */}
          <Menu
            anchorEl={anchorEl}
            open={openMenu}
            onClose={handleCloseMenu}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            sx={{
              "& .MuiPaper-root": {
                minWidth: 220,
                borderRadius: 2,
                p: 1,
              },
            }}
          >
            {/* User Info Section */}
            <Box sx={{ px: 2, py: 1.5, textAlign: "center" }}>
              <Avatar sx={{ width: 56, height: 56, mx: "auto", mb: 1 }}>
                {getInitials(user.name)}
              </Avatar>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                {user.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", direction: "ltr" }}
              >
                {user.phone}
              </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            {/* Logout Button */}
            <MenuItem
              onClick={handleLogout}
              sx={{
                justifyContent: "center",
                color: "error.main",
                fontWeight: "bold",
              }}
            >
              تسجيل الخروج
            </MenuItem>
          </Menu>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}
